/**
 * BoardingPassModal
 *
 * Modal for viewing, downloading, and sending boarding passes to JetSetters
 * - View boarding pass in print preview
 * - Download as PDF
 * - Send to JetSetter's email
 * - Displays passenger and flight information
 */

import { useState } from 'react';
import toast from 'react-hot-toast';
import { generateBoardingPassPDF, sendBoardingPassToEmail } from '../../../utils/boardingPassGenerator';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

const BoardingPassModal = ({ booking, flightData, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Handle view boarding pass
  const handleViewBoardingPass = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/bookings/${booking.reference_number}/boarding-pass-data`,
        { headers: authHeaders() }
      );
      const data = await res.json();

      if (!data.success) {
        toast.error(data.message || 'Failed to load boarding pass data');
        return;
      }

      // Generate and display PDF
      generateBoardingPassPDF(
        data.data,
        `boarding-pass-${booking.reference_number}.pdf`
      );
      toast.success('Boarding pass opened in print preview');
    } catch (err) {
      console.error('Error loading boarding pass:', err);
      toast.error('Failed to load boarding pass');
    } finally {
      setLoading(false);
    }
  };

  // Handle send boarding pass to email
  const handleSendBoardingPassEmail = async () => {
    setSendingEmail(true);
    try {
      const data = await sendBoardingPassToEmail(booking.reference_number, API_BASE);

      if (!data.success) {
        toast.error(data.message || 'Failed to send boarding pass');
        return;
      }

      toast.success(`Boarding pass sent to ${data.data.recipient_email}`);
      onClose();
    } catch (err) {
      console.error('Error sending boarding pass:', err);
      toast.error('Failed to send boarding pass to email');
    } finally {
      setSendingEmail(false);
    }
  };

  const passengers = booking.passengers || [];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Boarding Pass</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {booking.reference_number} · {passengers.length} Passenger(s)
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors text-xl font-bold"
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {/* Flight Information */}
            <div className="mb-6 p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg border border-yellow-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Departure */}
                <div className="text-center">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">From</p>
                  <p className="text-2xl font-bold text-yellow-700">{flightData?.dep_airport || 'N/A'}</p>
                  <p className="text-sm text-gray-600 mt-1">{fmtDate(booking.departure_date_time)}</p>
                </div>

                {/* Arrow */}
                <div className="flex items-center justify-center">
                  <div className="text-3xl font-bold text-yellow-600">→</div>
                </div>

                {/* Arrival */}
                <div className="text-center">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">To</p>
                  <p className="text-2xl font-bold text-yellow-700">{flightData?.arr_airport || 'N/A'}</p>
                  <p className="text-sm text-gray-600 mt-1">{fmtDate(booking.arrival_date_time)}</p>
                </div>
              </div>
            </div>

            {/* Flight Details */}
            <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 min-w-0">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Flight</p>
                <p className="text-lg font-bold text-gray-900 truncate">{flightData?.flight_number || 'N/A'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 min-w-0">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Aircraft</p>
                <p className="text-lg font-bold text-gray-900 truncate">{flightData?.aircraft_name || 'N/A'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 min-w-0">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Reg No.</p>
                <p className="text-lg font-bold text-gray-900 truncate">{flightData?.registration_number || 'N/A'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 min-w-0">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Booking Ref</p>
                <p className="text-lg font-bold text-gray-900 truncate" title={booking.reference_number}>{booking.reference_number}</p>
              </div>
            </div>

            {/* Passengers */}
            <div className="mb-6">
              <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Passengers ({passengers.length})</h3>
              <div className="space-y-2">
                {passengers.map((passenger, idx) => (
                  <div key={passenger.id} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-semibold text-gray-900">
                      {idx + 1}. {passenger.first_name} {passenger.last_name}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      DOB: {new Date(passenger.date_of_birth).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} | 
                      Email: {passenger.email}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Booking Status */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Status</p>
              <div className="flex items-center gap-2">
                <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                  {booking.booking_status || 'Confirmed'}
                </span>
                <span className="text-sm text-gray-600">
                  Ready for check-in
                </span>
              </div>
            </div>
          </div>

          {/* Footer - Action Buttons */}
          <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm font-semibold border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleViewBoardingPass}
              disabled={loading || sendingEmail}
              className="inline-flex items-center gap-2 px-5 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition-all duration-200 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              title="View boarding pass in print preview"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span>{loading ? 'Loading...' : 'View'}</span>
            </button>
            <button
              onClick={handleSendBoardingPassEmail}
              disabled={loading || sendingEmail}
              className="inline-flex items-center gap-2 px-5 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 hover:border-purple-300 transition-all duration-200 font-medium text-sm disabled:opacity-75 disabled:cursor-not-allowed"
              title={sendingEmail ? 'Sending boarding pass...' : "Send boarding pass to JetSetter's email"}
            >
              <svg 
                className={`w-4 h-4 transition-transform ${sendingEmail ? 'animate-spin' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>{sendingEmail ? 'Sending...' : 'Send Email'}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BoardingPassModal;
